<template>
    <ChatAgentToolFrame is-actionable :is-complete="isSubmitted" completion-message="Your review has been submitted to Workday.">
        <div v-if="!isSubmitted" class="nadia-chat flex flex-col gap-1" data-testid="chat-push-review-to-workday">
            <div class="flex items-center justify-between gap-2">
                <div class="text-valence-primary-500">{{ draftTitle }}</div>
                <BaseButton theme="tertiary" size="sm" data-testid="toggle-edit-review" :disabled="loading || saving" @click="toggleEdit">
                    <i class="bi" :class="editing ? 'bi-check-lg' : 'bi-pencil'" aria-hidden="true"></i>
                    {{ editing ? "Done" : "Edit" }}
                </BaseButton>
            </div>
            <div class="text-valence-grey-750">Generated from your conversation. Review or edit it below, then submit it when you're ready.</div>
        </div>
        <div v-if="!isSubmitted" class="flex flex-col gap-4">
            <template v-if="editing">
                <section v-for="section in submittableSections" :key="section.field" class="flex flex-col gap-1">
                    <h4 class="body-semibold text-valence-grey-800">{{ section.title }}</h4>
                    <BaseTextarea
                        v-model:input="comments[section.field]"
                        rows="4"
                        :maxlength="MAX_COMMENT_LENGTH"
                        :disabled="loading || saving"
                        :data-testid="`edit-${section.field}`"
                        :aria-label="section.title"
                        :placeholder="`Nothing extracted for ${section.title} — add a comment if you'd like.`"
                        class="caret-valence-primary-500 text-valence-grey-800"
                        @blur="saveDraft" />
                </section>
            </template>
            <template v-else>
                <section v-for="section in visibleSections" :key="section.field" class="flex flex-col gap-1">
                    <h4 class="body-semibold text-valence-grey-800">{{ section.title }}</h4>
                    <p class="body-small text-valence-grey-750 whitespace-pre-line">{{ section.comment }}</p>
                </section>
            </template>
        </div>
        <div v-if="!isSubmitted" class="body-small text-valence-grey-600" aria-live="polite" aria-atomic="true">
            <span v-if="statusMessage">{{ statusMessage }}</span>
        </div>
        <div role="alert" aria-atomic="true">
            <span v-if="noticeMessage" class="body-small text-red-700">{{ noticeMessage }}</span>
        </div>
        <BaseButton
            v-if="!isSubmitted"
            :disabled="loading || saving"
            class="flex justify-center items-center gap-1"
            data-testid="submit-review"
            theme="primary"
            @click="handleSubmit">
            <LoadingSpinner v-if="loading" class="h-4" aria-label="Submitting review to Workday" />Submit to Workday
        </BaseButton>
    </ChatAgentToolFrame>
</template>

<script setup lang="ts">
import { ChatAction } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { useActionHandler } from "~vue/composables/useActionHandler";
import { useDeferredLoading } from "~vue/composables/useDeferredLoading";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "~vue/Moments/constants";
import type { ParseActionsPushReviewToWorkdayAction, UpdateActionState } from "~vue/types/chat";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, reactive, ref } from "vue";

import ChatAgentToolFrame from "./ChatAgentToolFrame.vue";

type ExtractedPerfReview = ParseActionsPushReviewToWorkdayAction["action_params"]["extracted"];
type CommentField = keyof ExtractedPerfReview;
type ReviewLine = Pick<ParseActionsPushReviewToWorkdayAction, "action_params" | "action_state">;
type DraftPayload = { draft_version?: number; extracted?: ExtractedPerfReview };

interface ActionResult {
    success?: boolean;
    payload?: DraftPayload & { error?: string | null };
}

interface Props {
    line: ReviewLine;
    lineIdx: number;
    messageId: number;
}

const MAX_COMMENT_LENGTH = 10_000;
const SECTION_DEFS: Array<{ field: CommentField; title: string; sectionKey: string | null }> = [
    { field: "goals_comment", title: "Goals", sectionKey: "goals" },
    { field: "values_comment", title: "Values & Competencies", sectionKey: "competencies" },
    { field: "performance_comment", title: "Performance", sectionKey: "performance" },
    { field: "overall_comment", title: "Overall Summary", sectionKey: null },
];

const props = defineProps<Props>();

const { execute } = useActionHandler();
const updateActionState = inject<UpdateActionState>("updateActionState")!;
const coachingSessionUuid = inject<string>("coachingSessionUuid")!;
const { loading, start: startLoading, stop: stopLoading } = useDeferredLoading();

const editing = ref(false);
const saving = ref(false);
const noticeMessage = ref("");
const draftVersion = ref(props.line.action_params.draft_version ?? 0);
let queuedSaveCount = 0;
let pendingSave: Promise<boolean> = Promise.resolve(true);

const isSubmitted = computed(() => Boolean(props.line.action_state?.submitted));
const draftTitle = computed(() => {
    const title = props.line.action_params.review_title?.trim();
    return title ? `${title} — Draft` : "Performance Review — Draft";
});

function extractedComments(extracted: Partial<ExtractedPerfReview> = props.line.action_params.extracted): ExtractedPerfReview {
    return {
        goals_comment: extracted.goals_comment ?? "",
        values_comment: extracted.values_comment ?? "",
        performance_comment: extracted.performance_comment ?? "",
        overall_comment: extracted.overall_comment ?? "",
    };
}

const comments = reactive<ExtractedPerfReview>(extractedComments());
const savedComments = ref<ExtractedPerfReview>(extractedComments());
const dirtyFields = computed(() => SECTION_DEFS.map(({ field }) => field).filter((field) => comments[field] !== savedComments.value[field]));
const hasUnsavedEdits = computed(() => dirtyFields.value.length > 0);
const statusMessage = computed(() => {
    if (saving.value) {
        return "Saving your edits…";
    }
    if (hasUnsavedEdits.value) {
        return "Edited — not yet saved";
    }
    if (draftVersion.value > 0) {
        return "Edited — not yet submitted";
    }
    return "";
});
const submittableSections = computed(() => {
    const submittable = props.line.action_params.submittable_section_keys;
    const labels = props.line.action_params.section_labels ?? {};
    return SECTION_DEFS.filter(({ sectionKey }) => sectionKey === null || !submittable || submittable.includes(sectionKey)).map((section) => ({
        ...section,
        title: (section.sectionKey && labels[section.sectionKey]) || section.title,
    }));
});
const visibleSections = computed(() =>
    submittableSections.value.map(({ field, title }) => ({ field, title, comment: comments[field] })).filter((section) => section.comment.trim()),
);

function updateStoredDraft(extracted: ExtractedPerfReview) {
    updateActionState({
        message_id: props.messageId,
        lineIdx: props.lineIdx,
        action_params: { extracted: { ...extracted }, draft_version: draftVersion.value },
    });
}

function recordSavedDraft(payload: DraftPayload, edits: Partial<ExtractedPerfReview>) {
    const serverDraft = extractedComments(payload.extracted ?? { ...savedComments.value, ...edits });
    if (payload.draft_version !== undefined) {
        draftVersion.value = payload.draft_version;
    }
    for (const field of SECTION_DEFS.map(({ field }) => field)) {
        if (field in edits && comments[field] === edits[field]) {
            comments[field] = serverDraft[field];
        }
    }
    savedComments.value = serverDraft;
    updateStoredDraft(serverDraft);
}

function adoptServerDraft(payload: DraftPayload) {
    if (payload.draft_version !== undefined) {
        draftVersion.value = payload.draft_version;
    }
    const serverDraft = extractedComments(payload.extracted);
    Object.assign(comments, serverDraft);
    savedComments.value = serverDraft;
    updateStoredDraft(serverDraft);
}

function toggleEdit() {
    editing.value = !editing.value;
    if (!editing.value) {
        void saveDraft();
    } else {
        logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}edit_review_draft`, { session_id: coachingSessionUuid }, coachingSessionUuid);
    }
}

function saveDraft(): Promise<boolean> {
    if (!hasUnsavedEdits.value) {
        return saving.value ? pendingSave : Promise.resolve(true);
    }
    queuedSaveCount += 1;
    saving.value = true;
    pendingSave = pendingSave
        .then(() => doSave())
        .finally(() => {
            queuedSaveCount -= 1;
            saving.value = queuedSaveCount > 0;
        });
    return pendingSave;
}

async function doSave(): Promise<boolean> {
    if (!hasUnsavedEdits.value) {
        return true;
    }
    noticeMessage.value = "";
    const edits: Partial<ExtractedPerfReview> = {};
    for (const field of dirtyFields.value) {
        edits[field] = comments[field];
    }
    try {
        const result: ActionResult = await execute(
            ChatAction.PUSH_REVIEW_TO_WORKDAY,
            {
                message_id: props.messageId,
                lineIdx: props.lineIdx,
                subaction: "save_draft",
                base_version: draftVersion.value,
                action_params: edits,
            },
            { sync: true },
        );
        if (result?.success) {
            recordSavedDraft(result.payload ?? {}, edits);
            return true;
        }
        noticeMessage.value = result?.payload?.error ?? "We couldn't save your edits. Please try again.";
        if (result?.payload?.extracted) {
            adoptServerDraft(result.payload);
        }
    } catch (error) {
        logError(error);
        noticeMessage.value = "We couldn't save your edits. Please try again.";
    }
    return false;
}

async function handleSubmit() {
    startLoading();
    noticeMessage.value = "";
    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}push_to_workday_clicked`, { session_id: coachingSessionUuid }, coachingSessionUuid);

    try {
        if (!(await saveDraft()) || hasUnsavedEdits.value) {
            return;
        }
        const result: ActionResult = await execute(
            ChatAction.PUSH_REVIEW_TO_WORKDAY,
            {
                message_id: props.messageId,
                lineIdx: props.lineIdx,
                base_version: draftVersion.value,
            },
            { sync: true },
        );
        if (result?.success) {
            updateActionState({
                message_id: props.messageId,
                lineIdx: props.lineIdx,
                action_state: {
                    dismissed: props.line.action_state?.dismissed ?? false,
                    minimized: props.line.action_state?.minimized ?? false,
                    submitted: true,
                },
            });
        } else {
            noticeMessage.value = result?.payload?.error ?? "We couldn't submit your review to Workday. Please try again later.";
            if (result?.payload?.extracted) {
                adoptServerDraft(result.payload);
            }
        }
    } catch (error) {
        logError(error);
        noticeMessage.value = "We couldn't submit your review to Workday. Please try again later.";
    } finally {
        stopLoading();
    }
}
</script>
